import React, { useEffect, useState, useMemo } from "react";
import Navbar from "../components/Navbar";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Footer from "../Footer";
import axios from "axios";
import DataTable from "react-data-table-component";
import { toTitleCase } from "../helper/formatCarName";
import { SkeletonTable } from "../components/SkeletonLoader";
import { BtnLoader } from "../components/PageLoader";

const AllMyOffers = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading2, setLoading2] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectBidId, setRejectBidId] = useState(null);
  const [bidUserId, setBidUserId] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  const getDataFromLocalStorage = (key) => {
    try {
      const serializedData = localStorage.getItem(key);
      return serializedData ? JSON.parse(serializedData) : null;
    } catch (error) {
      console.error("Error getting data from localStorage:", error);
      return null;
    }
  };

  const storedUserPreferences = useMemo(
    () => getDataFromLocalStorage("userData"),
    []
  );

  useEffect(() => {
    if (!storedUserPreferences) {
      Swal.fire({
        title: "Unauthorized",
        text: "You need to be logged in to access this page",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#00889C",
        cancelButtonColor: "#d33",
        confirmButtonText: "Log In Now",
      }).then((result) => {
        navigate(result.isConfirmed ? "/login" : "/");
      });
      return;
    } else if (storedUserPreferences?.role !== "dealer") {
      Swal.fire({
        title: "Unauthorized Access",
        text: "Only dealers can access this!",
        icon: "warning",
        confirmButtonColor: "#00889C",
        confirmButtonText: "Back To Home",
      }).then(() => navigate("/"));
    }
  }, [storedUserPreferences, navigate]);

  const getAllMyCar = async () => {
    if (!storedUserPreferences?.id) return;
    try {
      const response = await axios.post(
        window.CARLOCA_API + "/get/offer/bids",
        { owner_id: storedUserPreferences?.id }
      );
      if (response?.data?.status) {
        setData(response?.data?.biddings?.pending);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    getAllMyCar();
  }, [storedUserPreferences?.id]);

  function formatEuroAmount(amount) {
    if (!amount && amount !== 0) return "—";
    const parts = amount.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  }

  const deleteMyOffers = async () => {
    if (selectedRowIds.length === 0) {
      Swal.fire(
        "No Selection",
        "Please select at least one offer to delete.",
        "warning"
      );
      return;
    }

    Swal.fire({
      title: "Delete Offers?",
      text: "You are about to delete selected offers!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Delete!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          setLoading2(true);
          const response = await axios.post(
            window.CARLOCA_API + "/delete/all-selected-bids",
            { bid_ids: selectedRowIds, owner_id: storedUserPreferences?.id }
          );

          setLoading2(false);
          if (response?.data?.status) {
            Swal.fire(
              "Deleted!",
              "Selected offers have been removed.",
              "success"
            );
            getAllMyCar();
          } else {
            Swal.fire("Error", response?.data?.message, "error");
          }
        } catch (error) {
          setLoading2(false);
          console.error("Error deleting offers:", error);
        }
      }
    });
  };

  const deleteMyBid = async (id) => {
    Swal.fire({
      title: "Delete Bid?",
      text: "Do you really want to delete this bid?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Delete!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axios.post(
            window.CARLOCA_API + "/delete/bid",
            { bid_id: id, owner_id: storedUserPreferences?.id }
          );

          if (response?.data?.status) {
            getAllMyCar();
          } else {
            Swal.fire("Error", response?.data?.message, "error");
          }
        } catch (error) {
          console.error("Error deleting bid:", error);
        }
      }
    });
  };
  const handleAccept = async (id, user_id) => {
    Swal.fire({
      title: "Accept Offer?",
      text: "You want to accept this offer?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Accept",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axios.post(
            window.CARLOCA_API + "/status/bidding",
            {
              user_id: user_id,
              bid_id: id,
              status: "accept",
            }
          );
          if (response?.data?.status) {
            Swal.fire("Accepted!", "The offer has been accepted.", "success");
            getAllMyCar();
          }
        } catch (error) {
          console.log("An error occurred while fetching data.", error);
        }
      }
    });
  };

  const handleReject = (id, userId) => {
    setRejectBidId(id);
    setBidUserId(userId);
    setRejectReason("");
  };

  const submitRejection = async () => {
    if (!rejectReason.trim()) {
      Swal.fire("Error", "Please enter a rejection reason.", "error");
      return;
    }
    try {
      const response = await axios.post(
        window.CARLOCA_API + "/status/bidding",
        {
          user_id: bidUserId,
          bid_id: rejectBidId,
          status: "reject",
          reason: rejectReason,
        }
      );
      if (response?.data?.status) {
        setRejectReason("");
        document.getElementById('closeRejectModal').click();
        Swal.fire("Rejected!", "The offer has been rejected.", "success");
        getAllMyCar();
      }
    } catch (error) {
      console.error("Error rejecting bid:", error);
    }
  };
  
  const columnsData = [
    {
      name: "Vehicle",
      minWidth: "220px",
      cell: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "6px 0" }}>
          <img
            src={
              row?.car?.image
                ? `${window.CARLOCA_UPLOADS}/cars/${row.car.image}`
                : "img/noimg.jpeg"
            }
            alt={row?.car?.make}
            style={{
              width: "48px",
              height: "36px",
              borderRadius: "6px",
              objectFit: "cover",
              border: "1px solid #e8ecf0",
              flexShrink: 0,
            }}
          />
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontWeight: 600, color: "#1a1a2e", fontSize: "13px", textTransform: "capitalize" }}>
              {toTitleCase(row?.car?.make)} {toTitleCase(row?.car?.model)}
            </div>
            <div style={{ fontSize: "11px", color: "#8b95a5" }}>
              {row?.car?.year} · {row?.car?.body_type || "—"}
            </div>
          </div>
        </div>
      ),
      sortable: true,
      sortFunction: (a, b) => {
        const nameA = `${a?.car?.make} ${a?.car?.model}`.toLowerCase();
        const nameB = `${b?.car?.make} ${b?.car?.model}`.toLowerCase();
        return nameA.localeCompare(nameB);
      },
    },
    {
      name: "Name",
      selector: (row) => row?.bidder?.role === "dealer"
        ? (row?.bidder?.dealerName || row?.bidder?.first_name)
        : row?.bidder?.first_name,
      sortable: true,
      wrap: true,
    },
    {
      name: "Email",
      selector: (row) => row?.bidder?.email,
      sortable: true,
      minWidth: "150px",
      wrap: true,
      cell: (row) => (
        <span style={{ fontSize: "12px", color: "#555", wordBreak: "break-all" }}>
          {row?.bidder?.email}
        </span>
      ),
    },
    { 
      name: "Phone", 
      selector: (row) => row?.bidder?.phone, 
      wrap: true,
      cell: (row) => (
        <span style={{ fontSize: "12px", color: "#555" }}>
          {row?.bidder?.phone || "—"}
        </span>
      ), 
    },
    { 
      name: "Type", 
      selector: (row) => row?.bid_request, 
      wrap: true,
      cell: (row) => (
        <span style={{ textTransform: "capitalize", fontSize: "12px", fontWeight: 500 }}>
          {row?.bid_request}
        </span>
      ),
    },
    {
      name: "Price Offered",
      cell: (row) => (
        <span style={{ fontWeight: 700, color: "#00889c", fontSize: "13px" }}>
          £{formatEuroAmount(row.amount)}
        </span>
      ),
      sortable: true,
      sortFunction: (a, b) => (a.amount || 0) - (b.amount || 0),
      width: "120px",
    },
    {
      name: "Actions",
      width: "170px",
      cell: (row) => {
        const isAccepted = row.status === "accept";
        const isRejected = row.status === "reject";
        
        return (
          <div style={{ display: "flex", gap: "6px" }}>
            <button
              className={`btn btn-sm ${isAccepted ? "btn-success" : "btn-outline-success"}`}
              style={{ fontSize: "12px", padding: "4px 10px", borderRadius: "6px", pointerEvents: isAccepted ? "none" : "auto" }}
              onClick={() => handleAccept(row.id, row.user_id)}
              disabled={isAccepted}
            >
              {isAccepted ? "Accepted" : "Accept"}
            </button>
            <button
              className={`btn btn-sm ${isRejected ? "btn-danger" : "btn-outline-danger"}`}
              style={{ fontSize: "12px", padding: "4px 10px", borderRadius: "6px", pointerEvents: isRejected ? "none" : "auto" }}
              onClick={() => handleReject(row.id, row.user_id)}
              data-bs-toggle="modal"
              data-bs-target="#rejectModal"
              disabled={isRejected}
            >
              {isRejected ? "Rejected" : "Reject"}
            </button>
          </div>
        );
      },
    },
    {
      name: "Submitted",
      selector: (row) => row.created_at_human,
      sortable: true,
      wrap: true,
      cell: (row) => (
        <span style={{ fontSize: "11px", color: "#8b95a5" }}>
          {row.created_at_human}
        </span>
      ),
    },
    {
      name: "Delete",
      cell: (row) => (
        <button
          onClick={() => deleteMyBid(row.id)}
          type="button"
          title="Delete"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "30px",
            height: "30px",
            borderRadius: "6px",
            border: "1px solid #fde8e8",
            background: "#fff",
            cursor: "pointer",
            transition: "all .2s",
            color: "#ef4444",
            fontSize: "13px",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = "#ef4444"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#ef4444"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#ef4444"; e.currentTarget.style.borderColor = "#fde8e8"; }}
        >
          <i className="fa fa-trash"></i>
        </button>
      ),
      width: "60px",
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      center: true,
    },
  ];

  const filteredItems = useMemo(
    () =>
      data.filter((item) =>
        ["make", "model", "body_type", "color"].some((key) =>
          item?.car?.[key]?.toLowerCase()?.includes(filterText.toLowerCase())
        )
      ),
    [data, filterText]
  );
  function backto() {
    window.history.back();
  }

  const subHeaderComponent = (
    <div style={{ display: "flex", alignItems: "center", gap: "12px", width: "100%", flexWrap: "wrap", justifyContent: "space-between" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#6b7280", fontSize: "13px" }}>
        <span style={{ fontWeight: 600, color: "#1a1a2e" }}>{filteredItems.length}</span> offers
        {selectedRowIds.length > 0 && (
          <span style={{ background: "rgba(0,136,156,0.1)", color: "#00889c", padding: "2px 8px", borderRadius: "10px", fontSize: "11px", fontWeight: 600 }}>
            {selectedRowIds.length} selected
          </span>
        )}
      </div>
      <div style={{ position: "relative", minWidth: "200px", maxWidth: "280px" }}>
        <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "12px" }}></i>
        <input
          type="text"
          placeholder="Search make, model, colour..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 12px 8px 32px",
            borderRadius: "8px",
            border: "1.5px solid #e5e7eb",
            fontSize: "13px",
            fontFamily: "'Poppins', sans-serif",
            color: "#333",
            outline: "none",
            transition: "border-color .2s, box-shadow .2s",
            background: "#fafbfc",
          }}
          onFocus={(e) => { e.target.style.borderColor = "#00889c"; e.target.style.boxShadow = "0 0 0 3px rgba(0,136,156,0.1)"; }}
          onBlur={(e) => { e.target.style.borderColor = "#e5e7eb"; e.target.style.boxShadow = "none"; }}
        />
      </div>
    </div>
  );

  const tableCustomStyles = {
    table: {
      style: {
        borderRadius: "12px",
        overflow: "hidden",
        border: "1px solid #f0f3f5",
      },
    },
    headRow: {
      style: {
        background: "linear-gradient(135deg, #f8fafb 0%, #f0f4f5 100%)",
        borderBottom: "2px solid #e8ecf0",
        minHeight: "48px",
        fontSize: "11.5px",
        fontWeight: 700,
        color: "#5f6b7a",
        textTransform: "uppercase",
        letterSpacing: ".5px",
        fontFamily: "'Poppins', sans-serif",
      },
    },
    headCells: {
      style: {
        padding: "0 12px",
        fontFamily: "'Poppins', sans-serif",
      },
    },
    rows: {
      style: {
        minHeight: "58px",
        fontSize: "13px",
        fontFamily: "'Poppins', sans-serif",
        color: "#374151",
        borderBottom: "1px solid #f5f5f5",
        transition: "background .15s ease",
        "&:hover": {
          background: "#f7fafb !important",
        },
      },
    },
    cells: {
      style: {
        padding: "0 12px",
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #f0f3f5",
        fontSize: "13px",
        color: "#6b7280",
        fontFamily: "'Poppins', sans-serif",
        minHeight: "52px",
      },
      pageButtonsStyle: {
        borderRadius: "6px",
        height: "32px",
        width: "32px",
        padding: "4px",
        cursor: "pointer",
        transition: "all .2s",
        fill: "#6b7280",
        "&:hover:not(:disabled)": {
          background: "rgba(0,136,156,0.08)",
          fill: "#00889c",
        },
        "&:disabled": {
          opacity: 0.4,
          cursor: "not-allowed",
        },
      },
    },
    subHeader: {
      style: {
        padding: "16px 16px 12px",
        background: "transparent",
      },
    },
    checkbox: {
      style: {
        cursor: "pointer",
      },
    },
  };

  return (
    <>
      <Navbar />
      <div className="cl-page">
        <div className="container-fluid px-3 px-md-4">
          <div className="d-flex justify-content-between mb-3 align-items-center flex-wrap gap-2">
            <div>
              <h3 className="cl-page-title">Offers</h3>
              <p className="cl-page-subtitle">Manage all incoming offers</p>
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <button onClick={backto} type="button" className="cl-btn cl-btn-outline cl-btn-sm">
                <i className="fa fa-arrow-left me-1"></i>Back
              </button>
              <button onClick={deleteMyOffers} disabled={loading2 || selectedRowIds.length === 0} className="cl-btn cl-btn-danger cl-btn-sm">
                {loading2 ? <><BtnLoader /> Processing...</> : <><i className="fa-solid fa-trash me-1" style={{ fontSize: "11px" }}></i>Delete</>}
              </button>
              <Link to="/dealer/approved-offers" className="cl-btn cl-btn-outline cl-btn-sm">Approved Offers</Link>
              <Link to="/dealer/dashboard" className="cl-btn cl-btn-primary cl-btn-sm">Dashboard</Link>
            </div>
          </div>
          {pageLoading ? (
            <SkeletonTable />
          ) : (
            <div className="cl-card" style={{ overflow: "hidden" }}>
              <div className="cl-card-body" style={{ padding: 0 }}>
                {data.length === 0 ? (
                  <div className="cl-empty" style={{ padding: "48px 24px" }}>
                    <div className="cl-empty-icon"><i className="fa-solid fa-envelope-open"></i></div>
                    <h6 className="cl-empty-title">No Offers Available</h6>
                    <p className="cl-empty-text">You haven't received any offers yet.</p>
                  </div>
                ) : (
                  <DataTable
                    columns={columnsData}
                    data={filteredItems}
                    selectableRows
                    onSelectedRowsChange={({ selectedRows }) =>
                      setSelectedRowIds(selectedRows.map((row) => row.id))
                    }
                    pagination
                    paginationPerPage={15}
                    paginationRowsPerPageOptions={[10, 15, 25, 50]}
                    responsive
                    fixedHeader
                    fixedHeaderScrollHeight="calc(100vh - 280px)"
                    subHeader
                    subHeaderComponent={subHeaderComponent}
                    highlightOnHover
                    pointerOnHover
                    customStyles={tableCustomStyles}
                    noDataComponent={
                      <div style={{ padding: "40px 20px", textAlign: "center", color: "#9ca3af", fontSize: "14px" }}>
                        <i className="fa-solid fa-search" style={{ fontSize: "28px", marginBottom: "12px", display: "block", color: "#d1d5db" }}></i>
                        No offers match your search
                      </div>
                    }
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div
        className="modal fade"
        data-bs-backdrop="static"
        data-bs-keyboard="false"
        id="rejectModal"
        tabIndex="-1"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Deny Offer</h5>
              <button
                id="closeRejectModal"
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>
            <div className="modal-body">
              <label className="form-label">Reason for deny:</label>
              <select
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className="form-select form-control"
              >
                <option value="">-- Select Reason --</option>
                <option value="Price to Low">Price too Low</option>
                <option value="Currently Under Offer">
                  Currently Under Offer
                </option>
                <option value="Not Available">Not Available</option>
                <option value="Please call to discuss further">
                  Please call to discuss further
                </option>
              </select>
            </div>
            <div className="modal-footer border-0 pt-0">
              <button
                id="closeRejectModal"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
              >
                Close
              </button>
              <button className="btn btn-danger" onClick={submitRejection}>
                Reject Offer
              </button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default AllMyOffers;
